import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import type { Offer, OfferItemDetail } from '@siliconvault/shared';
import { Button } from '../ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { useRespondToOffer, useWithdrawOffer, useDismissOffer } from '../../hooks/useCommunity';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';
import styles from './OfferDetail.module.css';

interface Props {
  offer: Offer;
}

const badgeClassMap: Record<string, string> = {
  pending: styles.badgePending,
  accepted: styles.badgeAccepted,
  declined: styles.badgeDeclined,
  withdrawn: styles.badgeWithdrawn,
};

/** Group items by vault, preserving offered/requested side info. */
function groupByVault(items: OfferItemDetail[]): Array<{ vaultName: string | null; items: OfferItemDetail[] }> {
  const map = new Map<string | null, OfferItemDetail[]>();
  for (const item of items) {
    const key = item.vaultName ?? null;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  }
  return [...map.entries()].map(([vaultName, items]) => ({ vaultName, items }));
}

export function OfferDetail({ offer }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const respondMutation = useRespondToOffer();
  const withdrawMutation = useWithdrawOffer();
  const dismissMutation = useDismissOffer();

  const isSender = offer.fromUserId === user?.userId;
  const isRecipient = offer.toUserId === user?.userId;
  const isPending = offer.status === 'pending';

  const offeredItems = offer.items.filter((i) => i.side === 'offered');
  const requestedItems = offer.items.filter((i) => i.side === 'requested');
  const vaultGroups = groupByVault(offer.items);
  const hasVaults = offer.items.some((i) => i.vaultName);

  async function handleAccept() {
    try {
      await respondMutation.mutateAsync({ id: offer.id, action: 'accept' });
      toast(t('community.offerAccepted'), 'success');
    } catch (err: any) {
      toast(err?.message || t('community.offerActionFailed'), 'error');
    }
  }

  async function handleDecline() {
    try {
      await respondMutation.mutateAsync({ id: offer.id, action: 'decline' });
      toast(t('community.offerDeclined'), 'success');
    } catch (err: any) {
      toast(err?.message || t('community.offerActionFailed'), 'error');
    }
  }

  async function handleWithdraw() {
    try {
      await withdrawMutation.mutateAsync(offer.id);
      toast(t('community.offerWithdrawn'), 'success');
    } catch (err: any) {
      toast(err?.message || t('community.offerActionFailed'), 'error');
    }
  }

  async function handleDismiss() {
    try {
      await dismissMutation.mutateAsync(offer.id);
      toast(t('community.offerDismissed'), 'success');
      navigate({ to: '/offers' });
    } catch (err: any) {
      toast(err?.message || t('community.offerDismissFailed'), 'error');
    }
  }

  const statusBadgeClass = badgeClassMap[offer.status] || '';

  return (
    <div className={styles.detail}>
      {/* Meta card: parties, status badge, date */}
      <div className={styles.metaCard}>
        <div className={styles.metaHeader}>
          <span className={styles.metaTitle}>{t('community.tradeOffer')}</span>
          <span className={`${styles.badge} ${statusBadgeClass}`}>
            <span className={styles.badgeDot} />
            {offer.status}
          </span>
        </div>

        <div className={styles.parties}>
          <div className={styles.partyBlock}>
            <span className={styles.partyRole}>{t('community.from')}</span>
            <span className={styles.partyName}>{offer.fromUserName}</span>
          </div>
          <div className={styles.partyArrow}>
            <ArrowRight size={16} strokeWidth={1.75} />
          </div>
          <div className={styles.partyBlock}>
            <span className={styles.partyRole}>{t('community.to')}</span>
            <span className={styles.partyName}>{offer.toUserName}</span>
          </div>
        </div>

        <div className={styles.dateLine}>
          <span>{t('community.created')}: {new Date(offer.createdAt).toLocaleString()}</span>
          {offer.respondedAt && (
            <span> · {t('community.responded')}: {new Date(offer.respondedAt).toLocaleString()}</span>
          )}
        </div>
      </div>

      {offer.message && (
        <div className={styles.messageCard}>
          <div className={styles.messageLabel}>{t('community.message')}</div>
          <p className={styles.messageText}>{offer.message}</p>
        </div>
      )}

      {/* Items: vault-grouped layout when vaults exist, classic layout otherwise */}
      {hasVaults ? (
        <div className={styles.vaultTilesSection}>
          {vaultGroups.map((group) => {
            const groupOffered = group.items.filter((i) => i.side === 'offered');
            const groupRequested = group.items.filter((i) => i.side === 'requested');
            return (
              <div key={group.vaultName ?? '_none'} className={styles.vaultTile}>
                <div className={styles.vaultTileHeader}>
                  {group.vaultName ?? 'Unassigned'}
                </div>
                <div className={styles.vaultTileBody}>
                  {groupOffered.length > 0 && (
                    <div className={styles.vaultSideGroup}>
                      <span className={styles.vaultSideLabel}>{t('community.offeredItems')}</span>
                      <div className={styles.itemsGrid}>
                        {groupOffered.map((item) => (
                          <Link key={item.id} to="/inventory/$itemId" params={{ itemId: item.itemId }} search={{ from: undefined }} className={styles.itemThumbWrap}>
                            {item.itemImageUrl ? (
                              <img src={item.itemImageUrl} alt={item.itemName} className={styles.itemThumb} />
                            ) : (
                              <div className={styles.itemThumbPlaceholder} />
                            )}
                            <span className={styles.itemThumbName}>{item.itemName}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  {groupRequested.length > 0 && (
                    <div className={styles.vaultSideGroup}>
                      <span className={styles.vaultSideLabel}>{t('community.requestedItems')}</span>
                      <div className={styles.itemsGrid}>
                        {groupRequested.map((item) => (
                          <Link key={item.id} to="/inventory/$itemId" params={{ itemId: item.itemId }} search={{ from: undefined }} className={styles.itemThumbWrap}>
                            {item.itemImageUrl ? (
                              <img src={item.itemImageUrl} alt={item.itemName} className={styles.itemThumb} />
                            ) : (
                              <div className={styles.itemThumbPlaceholder} />
                            )}
                            <span className={styles.itemThumbName}>{item.itemName}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.itemsSection}>
          <div className={styles.itemsGroup}>
            <h3 className={styles.itemsGroupTitle}>{t('community.offeredItems')}</h3>
            {offeredItems.length === 0 ? (
              <div className={styles.noItems}>{t('community.noItemsOffered')}</div>
            ) : (
              <div className={styles.itemsGrid}>
                {offeredItems.map((item) => (
                  <Link key={item.id} to="/inventory/$itemId" params={{ itemId: item.itemId }} search={{ from: undefined }} className={styles.itemThumbWrap}>
                    {item.itemImageUrl ? (
                      <img src={item.itemImageUrl} alt={item.itemName} className={styles.itemThumb} />
                    ) : (
                      <div className={styles.itemThumbPlaceholder} />
                    )}
                    <span className={styles.itemThumbName}>{item.itemName}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className={styles.itemsGroup}>
            <h3 className={styles.itemsGroupTitle}>{t('community.requestedItems')}</h3>
            {requestedItems.length === 0 ? (
              <div className={styles.noItems}>{t('community.noItemsRequested')}</div>
            ) : (
              <div className={styles.itemsGrid}>
                {requestedItems.map((item) => (
                  <Link key={item.id} to="/inventory/$itemId" params={{ itemId: item.itemId }} search={{ from: undefined }} className={styles.itemThumbWrap}>
                    {item.itemImageUrl ? (
                      <img src={item.itemImageUrl} alt={item.itemName} className={styles.itemThumb} />
                    ) : (
                      <div className={styles.itemThumbPlaceholder} />
                    )}
                    <span className={styles.itemThumbName}>{item.itemName}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {offer.moneyAmount && (
        <div className={styles.moneyCard}>
          <span className={styles.moneyLabel}>{t('community.cashComponent')}</span>
          <span className={styles.moneyAmount}>{offer.moneyCurrency} {offer.moneyAmount}</span>
        </div>
      )}

      {isPending && (
        <div className={styles.actions}>
          {isRecipient && (
            <>
              <Button onClick={handleAccept} disabled={respondMutation.isPending}>
                {t('community.accept')}
              </Button>
              <div className={styles.actionsDivider} />
              <Button variant="danger" onClick={handleDecline} disabled={respondMutation.isPending}>
                {t('community.decline')}
              </Button>
            </>
          )}
          {isSender && (
            <Button variant="secondary" onClick={handleWithdraw} disabled={withdrawMutation.isPending}>
              {t('community.withdraw')}
            </Button>
          )}
        </div>
      )}

      {!isPending && (
        <div className={styles.actions}>
          <Button variant="secondary" onClick={handleDismiss} disabled={dismissMutation.isPending}>
            {t('community.dismissOffer')}
          </Button>
        </div>
      )}
    </div>
  );
}
